// scheduling/pages/Dashboard.jsx
// Unified scheduler dashboard — the single owner. Replaces the old three-way split
// (UnifiedDashboard shell + ManagerDashboard + StaffDashboard). Owns: one
// me-resolution + one deduped data layer (useSchedulerData), the role-gated tab
// strip, ?tab=/?job=/?backlog= URL state, the shared manager job-data, and badge
// counts. Renders per-view presentational children (Overview / MySchedule / Backlog
// / Jobs / OverBudget); each keeps only its own ephemeral UI state.

import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useUrlState } from '../../hooks/useUrlState';
import { useSchedulerData } from '../hooks/useSchedulerData';
import { fetchClosedJobsWithSchedule } from '../services/schedData';
import { deleteClosedJobFuture } from '../services/schedApi';
import { isLeadOf } from '../utils/schedConstants';
import { selectMyJobsBacklog, selectMyTimeBacklog } from '../utils/backlogSelectors';
import { computeManagerJobData } from '../utils/managerJobData';
import { useTourDemo } from '../../components/tour/tourStore';
import { buildDemoMyJob } from '../../components/tour/tourDemoData';
import MyScheduleView from '../components/dashboard/MyScheduleView';
import BacklogView from '../components/dashboard/BacklogView';
import OverviewView from '../components/dashboard/OverviewView';
import JobsView from '../components/dashboard/JobsView';
import PoolView from '../components/dashboard/PoolView';
import OverBudgetTab from './OverBudgetTab';
import ScheduleChooserModal from '../components/ScheduleChooserModal';

export default function Dashboard() {
  const { isSuperUser, email, username } = useAuth();
  const navigate = useNavigate();
  const data = useSchedulerData();
  const {
    meStaffId, meName, jobs, jobsWIP, schedule, myTimeWIP, myTimeSched, staff, deps,
    notifications, getDayCap, dayHoursByStaff, getContract, overBudget, loading, error,
    refreshSchedule, refreshDeps, patchJob,
  } = data;

  const tourDemo = useTourDemo();

  // "Do I lead any jobs?" — the single leads-jobs signal, including the guided-tour
  // demo job (which sets ManagerId/PartnerId = me), so the My Jobs tab shows during
  // the tour for any user.
  const jobsForLead = useMemo(
    () => (tourDemo && meStaffId ? [buildDemoMyJob(meStaffId), ...jobs] : jobs),
    [tourDemo, meStaffId, jobs],
  );
  const leadsJobs = useMemo(() => jobsForLead.some(j => isLeadOf(j, meStaffId)), [jobsForLead, meStaffId]);

  const staffById = useMemo(() => {
    const m = {};
    for (const s of staff) m[s.StaffId] = s;
    return m;
  }, [staff]);

  // Manager-side job data (enrichedJobs / myJobs / wipByJob / hrsByJob / conflicts) —
  // computed once here, shared by OverviewView + JobsView. Gated so plain staff who
  // lead nothing never pay for the enrich + firm-wide conflict scan. Uses the
  // demo-augmented job list so the tour's sample job appears under My Jobs.
  const needsManagerData = leadsJobs || isSuperUser;
  const EMPTY_MGR = useMemo(() => ({ enrichedJobs: [], myJobs: [], wipByJob: {}, hrsByJob: {}, conflicts: [] }), []);
  const managerJobData = useMemo(
    () => (needsManagerData
      ? computeManagerJobData({ jobs: jobsForLead, schedule, jobsWIP, staff, meStaffId, getDayCap, dayHoursByStaff })
      : EMPTY_MGR),
    [needsManagerData, jobsForLead, schedule, jobsWIP, staff, meStaffId, getDayCap, dayHoursByStaff, EMPTY_MGR],
  );

  // Selected job (detail sidebar) — owner-owned URL state (?job=), shared by
  // Overview's "View Job" action + JobsView's table/sidebar.
  const [selectedJobId, setSelectedJobId] = useUrlState('job', null, {
    serialize: v => (v == null ? '' : String(v)),
    deserialize: s => (s ? parseInt(s, 10) : null),
  });

  // ── Schedule chooser (hoisted from BacklogView so it survives on EITHER tab) ──
  // Opened from a backlog card (BacklogView) or the My Jobs sidebar (JobsView) via
  // openChooser. The "View detailed suggestions ↗" round-trip returns here
  // (?return=chooser); the restore effect below reopens the chooser from
  // pendingChooserDraft. origin drives where StaffSuggestions returns (backlog vs myjobs).
  const [chooser, setChooser] = useState(null); // { job, variant, subTab, origin, initial } | null
  // The firm-wide schedule here has no date bound, so the chooser can judge any window
  // from these without fetching its own.
  const chooserCapacity = useMemo(
    () => ({ getDayCap, dayHoursByStaff, getContract }),
    [getDayCap, dayHoursByStaff, getContract],
  );
  const openChooser = useCallback((job, { variant = 'myjobs', origin = 'backlog', subTab } = {}) => {
    setChooser({ job, variant, origin, subTab, initial: null });
  }, []);
  const chooserRestoredRef = useRef(false);
  useEffect(() => {
    if (chooserRestoredRef.current || !meStaffId) return;
    const raw = sessionStorage.getItem('pendingChooserDraft');
    if (!raw) return;
    chooserRestoredRef.current = true;
    sessionStorage.removeItem('pendingChooserDraft');
    try {
      const d = JSON.parse(raw);
      const picks = Array.isArray(d.pickedStaffIds) ? d.pickedStaffIds
        : (d.pickedStaffId != null ? [d.pickedStaffId] : []);
      const merged = [...new Set([...(d.selected || []), ...picks])];
      setChooser({
        job: { JobId: d.jobId, Name: d.jobName, ClientName: d.clientName, remaining: d.remaining, wipTotal: d.wipTotal || 0, dept: d.dept || null, Deadline: d.Deadline || null },
        variant: d.variant || 'myjobs',
        subTab: d.sub || 'mytime',
        origin: d.origin || 'backlog',
        initial: { selected: merged.length ? merged : undefined, showOthers: true, extraStaff: d.pickedStaffMeta || [], placeholders: d.placeholders || [], manualPicks: d.manualPicks || [], window: d.window || null },
      });
    } catch { /* ignore */ }
  }, [meStaffId]);


  // ── Tab badges (derived from the shared data — no separate fetch) ──
  // My Jobs excludes pooled jobs, so the badge has to as well — otherwise it counts
  // rows the tab no longer shows.
  const myJobCount = useMemo(
    () => jobs.filter(j => isLeadOf(j, meStaffId) && !j.PoolJobId).length,
    [jobs, meStaffId],
  );
  // Jobs I lead that are sitting in a pool waiting for a claimer. Derived from the
  // pool membership already carried on each job, so the Pool tab badge needs no
  // extra fetch.
  // PoolState matters here: jobs now carry their latest episode even after it's been
  // claimed (so they stay out of My Jobs for good), but this badge counts only what is
  // still WAITING for a claimer.
  const pooledWaitingCount = useMemo(
    () => jobs.filter(j => isLeadOf(j, meStaffId) && j.PoolJobId && j.PoolState === 'waiting').length,
    [jobs, meStaffId],
  );
  const allJobCount = jobs.length;
  const backlogCount = useMemo(() => {
    if (!meStaffId) return 0;
    const ids = new Set();
    for (const j of selectMyJobsBacklog({ jobs, jobsWIP, schedule, meStaffId, hidePostReview: true })) ids.add(j.JobId);
    for (const j of selectMyTimeBacklog({ myTimeWIP, myTimeSched, staffById, meStaffId, recentDays: 14 })) ids.add(j.JobId);
    return ids.size;
  }, [jobs, jobsWIP, schedule, myTimeWIP, myTimeSched, staffById, meStaffId]);
  const obPendingCount = useMemo(() => {
    if (!overBudget) return 0;
    const myUser = (email || username || '').toLowerCase();
    return (overBudget.jobs || []).filter(j =>
      j.status === 'pending' && (j.jmEmail || j.JMEmail || '').toLowerCase() === myUser
    ).length;
  }, [overBudget, email, username]);

  // Active view in the URL. 'alljobs'/'overbudget' are super-only; keep 'myjobs' in
  // the whitelist so a lead's deeplink isn't rejected pre-load, and render-guard the
  // non-lead case below (async-safe — never key validate on the async leadsJobs).
  const [activeView, setActiveView] = useUrlState('tab', 'overview', {
    validate: isSuperUser
      ? ['overview', 'schedule', 'backlog', 'myjobs', 'pool', 'alljobs', 'overbudget']
      : ['overview', 'schedule', 'backlog', 'myjobs', 'pool'],
  });

  // One-shot default-landing: a plain staffer (leads nothing, not super) who arrives
  // with no explicit ?tab lands on the near-empty Overview by default — send them to
  // My Schedule instead. Only redirects the default landing, never an explicit click.
  const hadExplicitTabRef = useRef(new URLSearchParams(window.location.search).has('tab'));
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current || loading) return;
    landedRef.current = true;
    if (!hadExplicitTabRef.current && !leadsJobs && !isSuperUser && activeView === 'overview') {
      setActiveView('schedule');
    }
  }, [loading, leadsJobs, isSuperUser, activeView, setActiveView]);

  // Job Pools pilot gate, resolved once in AppShell and shared via the outlet.
  const { poolAccess } = useOutletContext() || {};

  // Render guard: a non-lead on ?tab=myjobs (deeplink) renders My Schedule instead
  // of the empty jobs table. Same for ?tab=pool without pool access — a deeplink
  // must not get round the gate.
  const effectiveView = (activeView === 'myjobs' && !leadsJobs) ? 'schedule'
    : (activeView === 'pool' && !poolAccess) ? 'schedule'
      : activeView;

  // ── Closed jobs that still hold future-dated time ──────────────────────────
  // peSync retains a PE-completed job's rprs.Jobs row now (it used to delete the row AND
  // every schedule entry on it), so future hours can outlive the job they were booked
  // against. Each surface wants a different slice, so the fetch is scoped to the active
  // view rather than pulled firm-wide and filtered: My Schedule needs the staffId scope to
  // get MyFuture* populated, My Jobs wants only jobs I lead, All Jobs wants everything.
  const [closedJobs, setClosedJobs] = useState([]);
  const [closedScope, setClosedScope] = useState(null);

  const closedFilter = useMemo(() => {
    if (!meStaffId) return null;
    if (effectiveView === 'schedule') return { staffId: meStaffId };
    if (effectiveView === 'myjobs') return { managerId: meStaffId };
    if (effectiveView === 'alljobs') return {};
    return null;
  }, [effectiveView, meStaffId]);

  const refreshClosedJobs = useCallback(async () => {
    if (!closedFilter) { setClosedJobs([]); setClosedScope(null); return; }
    try {
      const rows = await fetchClosedJobsWithSchedule(closedFilter);
      setClosedJobs(rows || []);
      setClosedScope(effectiveView);
    } catch {
      // Non-fatal: the dashboard is useful without this section, so a failure here
      // hides it rather than breaking the view.
      setClosedJobs([]);
      setClosedScope(null);
    }
  }, [closedFilter, effectiveView]);

  useEffect(() => { refreshClosedJobs(); }, [refreshClosedJobs]);

  // Shared clear action. Two-step by design — the caller previews first so the confirm
  // shows the server's own count, not whatever the client happens to have loaded.
  const clearClosedJobFuture = useCallback(async (jobId, opts = {}) => {
    const res = await deleteClosedJobFuture(jobId, opts);
    if (!opts.preview) {
      await Promise.all([refreshSchedule(), refreshClosedJobs()]);
    }
    return res;
  }, [refreshSchedule, refreshClosedJobs]);

  // Only hand a view its own slice — a stale payload from the previous tab would
  // otherwise flash in before the refetch lands.
  const closedForView = closedScope === effectiveView ? closedJobs : [];

  const views = useMemo(() => {
    const v = [
      { key: 'overview', label: 'Overview' },
      { key: 'schedule', label: 'My Schedule' },
      { key: 'backlog', label: 'My Backlog', badge: backlogCount, badgeColor: 'blue' },
    ];
    if (leadsJobs) v.push({ key: 'myjobs', label: 'My Jobs', badge: myJobCount, badgeColor: 'blue' });
    // Pool tab (Job Pools, Trello #257) — where jobs go after leaving My Jobs.
    // Badge counts jobs still WAITING to be claimed: those are the ones that might
    // need a nudge. Claimed ones are history and shouldn't inflate a live count.
    // …and only for the pilot group while Job Pools is gated.
    if (leadsJobs && poolAccess) v.push({ key: 'pool', label: 'Pool', badge: pooledWaitingCount, badgeColor: 'blue' });
    if (isSuperUser) {
      v.push({ key: 'alljobs', label: 'All Jobs', badge: allJobCount, badgeColor: 'blue' });
      v.push({ key: 'overbudget', label: 'Over Budget', badge: obPendingCount || null, badgeColor: obPendingCount > 0 ? 'red' : 'blue' });
    }
    return v;
  }, [leadsJobs, poolAccess, isSuperUser, backlogCount, myJobCount, pooledWaitingCount, allJobCount, obPendingCount]);

  const isStaffView = effectiveView === 'schedule' || effectiveView === 'backlog';

  function renderContent() {
    if (error) {
      return (
        <div className="sched-placeholder">
          <h2>Error Loading Dashboard</h2>
          <p style={{ color: '#DC2626' }}>{error}</p>
        </div>
      );
    }
    if (loading) {
      return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading dashboard...</div></div>;
    }
    if (isStaffView) {
      return (
        <div className="sched-staff-root">
          {effectiveView === 'schedule'
            ? <MyScheduleView meStaffId={meStaffId} schedule={schedule} getDayCap={getDayCap} dayHoursByStaff={dayHoursByStaff}
                closedJobs={closedForView} clearClosedJobFuture={clearClosedJobFuture} />
            : <BacklogView
                meStaffId={meStaffId} meName={meName} leadsJobs={leadsJobs}
                jobs={jobs} jobsWIP={jobsWIP} schedule={schedule}
                myTimeWIP={myTimeWIP} myTimeSched={myTimeSched} staff={staff}
                getDayCap={getDayCap} dayHoursByStaff={dayHoursByStaff}
                openChooser={openChooser} />}
        </div>
      );
    }
    if (effectiveView === 'overbudget') {
      return isSuperUser ? <div className="sched-mgr"><OverBudgetTab myEmail={email} username={username} /></div> : null;
    }
    if (effectiveView === 'pool') {
      return <PoolView navigateToPool={() => navigate('/schedule/pool')} />;
    }
    if (effectiveView === 'overview') {
      return (
        <OverviewView
          myJobs={managerJobData.myJobs} enrichedJobs={managerJobData.enrichedJobs}
          wipByJob={managerJobData.wipByJob} conflicts={managerJobData.conflicts}
          schedule={schedule} notifications={notifications} meStaffId={meStaffId}
          setActiveView={setActiveView} setSelectedJobId={setSelectedJobId} />
      );
    }
    // myjobs → scope 'mine'; alljobs → scope 'all'
    const scope = effectiveView === 'alljobs' ? 'all' : 'mine';
    return (
      <JobsView scope={scope}
        enrichedJobs={managerJobData.enrichedJobs} myJobs={managerJobData.myJobs}
        wipByJob={managerJobData.wipByJob} hrsByJob={managerJobData.hrsByJob}
        staff={staff} deps={deps} meStaffId={meStaffId} isSuperUser={isSuperUser}
        email={email} username={username}
        selectedJobId={selectedJobId} setSelectedJobId={setSelectedJobId}
        refreshSchedule={refreshSchedule} refreshDeps={refreshDeps} patchJob={patchJob}
        openChooser={openChooser}
        closedJobs={closedForView} clearClosedJobFuture={clearClosedJobFuture}
        getDayCap={getDayCap} dayHoursByStaff={dayHoursByStaff} getContract={getContract} />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="sched-tabs" style={{ flexShrink: 0 }}>
        {views.map(v => (
          <button key={v.key}
            data-tour={`dash-tab-${v.key}`}
            className={`sched-tab ${activeView === v.key ? 'sched-tab--active' : ''}`}
            onClick={() => setActiveView(v.key)}>
            {v.label}
            {v.badge > 0 && (
              <span className={`sched-tab-badge sched-tab-badge--${v.badgeColor || 'blue'}`}>{v.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </div>

      {/* Schedule chooser — owned here (not in a view) so the round-trip can return
          to either the backlog or the My Jobs tab and still reopen it. */}
      {chooser && meStaffId && (
        <ScheduleChooserModal
          job={chooser.job}
          variant={chooser.variant}
          subTab={chooser.subTab || (chooser.variant === 'mytime' ? 'mytime' : 'myjobs')}
          origin={chooser.origin || 'backlog'}
          initial={chooser.initial || null}
          staff={staff}
          meStaffId={meStaffId}
          meName={meName || 'You'}
          capacity={chooserCapacity}
          onClose={() => setChooser(null)}
        />
      )}
    </div>
  );
}
